import React, { useEffect, useState } from "react";
import Loader from "../../../Components/GlobalLoader/Loader";
import CommonModal from "../../../Components/Modals/CommonModal";
import Table from "../../../Components/Tables/Table";
import ExportLayout from "../../../Layout/ExportLayout";
import ImportLayout from "../../../Layout/ImportLayout";
import PageHeader from "../../../Layout/PageHeader";
import Tabs from "../../../Shared/Tab/Tab";
import { Tab } from "../../../Shared/Tab/TabProps";
import { usePagination } from "../../../hooks/usePagination";
import { useSorting } from "../../../hooks/useSorting";
import { ApiResponse } from "../../../model/common/apiResponse";
import { useSearchStore } from "../../../store/search/searchStore";
import { fetchOrderList, fetchStatusWiseCounts, fetchSubTabStatusWiseCounts } from "../Orders/service/orderOverview.service";
import { ActiveTab, OrderListProps } from "../Orders/model/OrderList.type";
import { MdSyncProblem } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { formatSearchDate, isValidDate } from "../../../Utility/Utility";

const OrderList: React.FC<OrderListProps> = ({
  columns,
  db_columns,
  pageName,
  headerName,
  addUrl,
  listUrl,
  pageTabs,
  buttonStatus,
  isCreateBtnDisable = false,
  checkboxStatus = true,
  search_columns,
  clientId,
}) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>({
    order: pageTabs[0]?.order ?? 0,
    name: pageTabs[0]?.name ?? "",
  });
  const { currentPage, handlePageChange, perPage } = usePagination();
  const [showModalForImportAndExport, setShowModalForImportAndExport] =
    useState({
      importModal: false,
      exportModal: false,
    });
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [selectedRow, setSelectedRow] = useState<number>();
  const [show, setShow] = useState(false);
  const handleShow = () => setShow(!show);
  const [data, setData] = useState<ApiResponse<any> | null>(null);
  const statuses = pageTabs.map((tab) => tab.name);
  const [activeSubTab, setActiveSubTab] = useState("All");
  // const navigation = useNavigate();

  const [tabRecordcount, setTabRecordcount] = useState<{
    [key: string]: number;
  }>(
    statuses.reduce((acc, status) => {
      acc[status] = 0;
      return acc;
    }, {} as { [key: string]: number })
  );

  const { sortingData, handleSortChange } = useSorting(handlePageChange);

  const { searchTerm, resetSearch, previousSearchTerm, setPreviousTerm } =
    useSearchStore();
  useEffect(() => {
    if (searchTerm != previousSearchTerm && currentPage != 1) {
      handlePageChange(1);
    }

    let order: any[] = [];
    let conditions: any[] = [["sales_order.status", "=", activeTab.name]];

    if (activeTab.name === "In Progress") {
      if (activeSubTab === "partial") {
        conditions.push(["sales_order.allocation_status", "=", "partial"]);
      } else if (activeSubTab === "complete") {
        conditions.push(["sales_order.allocation_status", "=", "complete"]);
      }
    }

    let filterPayload: any[] = [];
    if (clientId) {
      filterPayload.push(["client.id", "=", clientId]);
    }
    conditions = conditions.concat(filterPayload);
    if (sortingData.column.column_name && sortingData.direction) {
      order = [
        {
          column: sortingData.column.column_name,
          dir: sortingData.direction,
        },
      ];
    }
    const formattedSearch = isValidDate(searchTerm) ? formatSearchDate(searchTerm) : searchTerm;

    const payload: any = {
      columns: db_columns,
      search_columns: search_columns,
      conditions: conditions,
      limit: perPage,
      page: currentPage,
      search: formattedSearch,
      order: [
        {
          column: "sales_order.order_number",
          dir: "desc",
        },
      ],
    };
    if (order.length > 0) {
      Object.assign(payload, { order });
    }

    setData(null);
    fetchStatusWiseCounts(statuses as string[], formattedSearch, filterPayload, search_columns)
      .then((res: any) => {
        if (res?.data) {
          setTabRecordcount((prevState) => {
            const updatedCounts: { [key: string]: number } = {
              ...prevState,
              [activeTab.name]: res.data[activeTab.name] || 0,
            };

            statuses.forEach((status) => {
              updatedCounts[status] = res.data[status] || 0;
            });
            return updatedCounts;
          });
        }
      })
      .then(() => {
        fetchOrderList(currentPage, payload).then((response) => {
          setData(response ?? null);
          setPreviousTerm(searchTerm);
        });
      })
      .catch((error) => {
        console.error("Error fetching status-wise counts:", error);
      });
  }, [currentPage, activeTab, searchTerm, sortingData, clientId, activeSubTab]);

  const handleSelectedRowsChange = (newSelectedRows: Set<number>) => {
    const row_no = Array.from(newSelectedRows)[0];
    setSelectedRow(data?.data?.results[row_no]?.id);
    setSelectedRows(newSelectedRows);
  };

  const onTabClick = (tab: Tab) => {
    setActiveTab({ order: tab.order ?? 0, name: tab.name ?? "new" });
    setActiveSubTab("All");
    handlePageChange(1);

    if (tab.name === "In Progress") {
      fetchSubTabStatusWiseCounts()
        .then((res: any) => {
          console.log('res', res);
          if (res?.data) {
            setTabRecordcount((prev) => ({
              ...prev,
              partial: res.data.allocation_status.partial || 0,
              complete: res.data.allocation_status.complete || 0,
            }));
          }
        })
        .catch((err) => {
          console.error("Error fetching sub-tab counts:", err);
        });
    }
  };

  const numberOfLinesEqZero = (row: any) => {
    return false;
  };

  const renderTable = (multiSelect?: boolean | null) => {
    return (
      <Table
        customClassName="sph-table-color"
        enableSorting={true}
        columns={columns}
        data={data?.data?.results || []}
        pagination={{
          recordsPerPage: perPage,
          totalRecords: data?.data?.recordsTotal,
          currentPage: currentPage,
          onCurrentPageChange: handlePageChange,
        }}
        multiselect={multiSelect}
        onSortChange={handleSortChange}
        sortingData={sortingData}
        onSelectedRowsChange={handleSelectedRowsChange}
        CheckboxStatus={checkboxStatus}
        checkboxDisableCondition={numberOfLinesEqZero}
      />
    );
  };

  const tabs = pageTabs.map((tab) => {
    if (tab.name === "In Progress") {
      const subTabs = [
        { key: "All", label: `All | ${tabRecordcount["In Progress"] ?? 0}` },
        { key: "partial", label: `Allocation Partial | ${tabRecordcount["partial"] ?? 0}` },
        { key: "complete", label: `Allocation Completed | ${tabRecordcount["complete"] ?? 0}` },
      ];
      return {
        ...tab,
        label: `${tab.tabLable} | ${tabRecordcount[tab.name] ?? 0}`,
        content: (
          <>
            <div className="sub-tab-bar d-flex gap-4 mb-3 bg-white">
              {subTabs.map((subTab) => (
                <div
                  key={subTab.key}
                  className={`sub-tab-item ${activeSubTab === subTab.key ? "active" : ""}`}
                  onClick={() => setActiveSubTab(subTab.key)}
                  style={{ cursor: "pointer", fontWeight: activeSubTab === subTab.key ? "bold" : "normal" }}
                >
                  {subTab.label}
                </div>
              ))}
            </div>
            {renderTable(tab.multiSelect)}
          </>
        ),
      };
    } else {
      return {
        ...tab,
        label: `${tab.tabLable} | ${tabRecordcount[tab.name] ?? 0}`,
        content: renderTable(tab.multiSelect),
      };
    }
  });

  return data ? (
    <>
      <PageHeader
        addUrl={selectedRow ? addUrl + "/" + selectedRow : addUrl}
        listUrl={listUrl}
        name={pageName}
        nameButtonStatus={
          isCreateBtnDisable ? selectedRows.size > 0 : buttonStatus
        }
        setShowModalForImportAndExport={setShowModalForImportAndExport}
        showModalForImportAndExport={showModalForImportAndExport}
      // moreOptions={[
      //           {
      //             name: "Failed Orders",
      //             icon: (
      //               <div className="position-relative">
      //                 <div className="red-dot text-center">0</div>
      //                 <MdSyncProblem
      //                   className=" cursor-pointer"
      //                   style={{ fontSize: 28 }}
      //                 />
      //               </div>
      //             ),
      //             functionality: () => {
      //               navigation("/outbound/orders/failed-orders");
      //             },
      //           },
      //         ]}
      />

      <div className="clearfix"></div>

      <h5 className="text-start mt-1 mb-3">{headerName}</h5>

      <div>
        <Tabs
          tabs={tabs}
          isCheckbox={true}
          onTabClick={onTabClick}
          active={activeTab.order}
        />
      </div>
      <div className="inbound-CheckIn-Table-Main-Div"></div>

      <CommonModal
        show={showModalForImportAndExport.importModal}
        handleClose={() =>
          setShowModalForImportAndExport((preValue: any) => {
            return { ...preValue, importModal: false };
          })
        }
        heading={"Import File"}
        content={<ImportLayout handleClose={handleShow} />}
        modalSize="modal-lg"
        footerButtonsActive={false}
        customClassNameForBody=""
        customClassNameForHeader="create-kit-orders"
      />
      <CommonModal
        show={showModalForImportAndExport.exportModal}
        handleClose={() =>
          setShowModalForImportAndExport((preValue: any) => {
            return { ...preValue, exportModal: false };
          })
        }
        heading={"Export"}
        content={<ExportLayout handleClose={handleShow} />}
        modalSize="modal-lg"
        footerButtonsActive={false}
        customClassNameForBody=""
        customClassNameForHeader="create-kit-orders"
      />
    </>
  ) : (
    <Loader />
  );
};
export default OrderList;